import React, { useEffect, useState } from "react";
import { Briefcase, BookOpen, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Move dialog used for stories AND booked trips.
 *  • stories  → only `year` can be picked (folders).
 *  • trips    → year folders + a "business, not pleasure" option.
 *
 * Years are pulled from /chapter-folders so the user can pick from any
 * folder they've already created, plus a "new year…" inline input.
 */
const TIDS = {
  root: "move-dialog",
  yearChip: (y) => `move-dialog-year-${y}`,
  businessChip: "move-dialog-business-chip",
  newYearInput: "move-dialog-new-year-input",
  confirm: "move-dialog-confirm",
  close: "move-dialog-close",
};

export default function MoveDialog({
  kind, // "story" | "trip"
  current, // { year, trip_type }
  onClose,
  onConfirm,
}) {
  const [folders, setFolders] = useState([]);
  const [selectedYear, setSelectedYear] = useState(current.year || null);
  const [selectedBusiness, setSelectedBusiness] = useState(
    kind === "trip" && current.trip_type === "business",
  );
  const [newYear, setNewYear] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chapter-folders");
        setFolders(data.folders || []);
      } catch {/* ignore */}
    })();
  }, []);

  const pickYear = (y) => {
    setSelectedYear(y);
    setSelectedBusiness(false);
    setNewYear("");
  };

  const pickBusiness = () => {
    setSelectedBusiness(true);
    setSelectedYear(null);
    setNewYear("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      let payload;
      if (selectedBusiness) {
        payload = { trip_type: "business" };
      } else {
        const y = newYear ? parseInt(newYear, 10) : selectedYear;
        if (!y || y < 1900 || y > 2100) {
          setBusy(false);
          return;
        }
        payload = kind === "trip"
          ? { year: y, trip_type: "pleasure" }
          : { year: y };
      }
      await onConfirm(payload);
    } finally { setBusy(false); }
  };

  const isSelected = (y) =>
    !selectedBusiness && !newYear && selectedYear === y;

  const canSubmit =
    selectedBusiness || (selectedYear != null) || (newYear && parseInt(newYear, 10) >= 1900);

  return (
    <div
      data-testid={TIDS.root}
      className="fixed inset-0 z-[70] bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-sm w-full overflow-hidden"
      >
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between">
          <span className="font-hand text-xl">
            move {kind === "trip" ? "trip" : "story"}
          </span>
          <button
            data-testid={TIDS.close}
            onClick={onClose}
            className="font-hand text-tb-gold tb-tap inline-flex items-center gap-1"
          >
            <X className="w-4 h-4" /> close
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <span className="font-hand text-tb-navy/65 text-sm flex items-center gap-1">
              <BookOpen className="w-4 h-4" /> chapter (year)
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {folders.map((f) => (
                <button
                  key={f.year}
                  data-testid={TIDS.yearChip(f.year)}
                  onClick={() => pickYear(f.year)}
                  className={`px-3 py-1.5 rounded-full font-hand text-sm border-2 tb-tap ${
                    isSelected(f.year)
                      ? "bg-tb-coral text-white border-tb-coral"
                      : "bg-tb-cream-2 text-tb-navy border-tb-cream-3 hover:border-tb-coral"
                  }`}
                >
                  {f.year}
                  {f.year === current.year && (
                    <span className="ml-1 text-[10px] opacity-70">(current)</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="font-hand text-tb-navy/65 text-sm">or new year</span>
            <input
              data-testid={TIDS.newYearInput}
              type="number" min="1900" max="2100"
              value={newYear}
              onChange={(e) => {
                setNewYear(e.target.value);
                setSelectedYear(null);
                setSelectedBusiness(false);
              }}
              placeholder="2028"
              className="w-full mt-1 px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>

          {kind === "trip" && (
            <div>
              <span className="font-hand text-tb-navy/65 text-sm flex items-center gap-1">
                <Briefcase className="w-4 h-4" /> or move to business
              </span>
              <button
                data-testid={TIDS.businessChip}
                onClick={pickBusiness}
                className={`mt-2 w-full px-3 py-2 rounded-xl font-hand text-base border-2 tb-tap flex items-center gap-2 justify-center ${
                  selectedBusiness
                    ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                    : "bg-tb-cream-2 text-tb-navy border-tb-cream-3 hover:border-tb-navy"
                }`}
              >
                <Briefcase className="w-4 h-4" /> business, not pleasure
                {current.trip_type === "business" && (
                  <span className="text-[10px] opacity-70">(current)</span>
                )}
              </button>
            </div>
          )}

          <button
            data-testid={TIDS.confirm}
            disabled={!canSubmit || busy}
            onClick={submit}
            className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-40"
          >
            {busy ? "moving…" : "move"}
          </button>
        </div>
      </div>
    </div>
  );
}
